import { useState } from "react";
import { toast } from "sonner";
import { Building2, Send } from "lucide-react";
import { api, formatApiError, trackEvent, getSid } from "../api";
import { useLang } from "../i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const ORG_TYPES = ["samorzad", "deweloper", "kancelaria", "posrednik", "inwestor", "urbanista", "inne"];
const EMPTY = { name: "", organization: "", email: "", role: "", org_type: "", message: "", consent: false };

export default function InstitutionalCta({ variant = "card" }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const openModal = () => {
    setOpen(true);
    trackEvent("inst_cta_view");
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads/institutional", {
        ...form, message: form.message || null, sid: getSid(),
      });
      toast.success(t("inst.success"));
      setOpen(false);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  return (
    <>
      {variant === "link" ? (
        <button
          data-testid="footer-institutional-link"
          onClick={openModal}
          className="mt-3 block text-xs font-medium text-slate-300 underline-offset-2 hover:text-white hover:underline"
        >
          {t("inst.footer")}
        </button>
      ) : (
        <div data-testid="inst-cta-card" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-start gap-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
              <Building2 size={18} />
            </span>
            <div>
              <h2 className="font-heading text-base font-bold text-slate-900">{t("inst.title")}</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{t("inst.text")}</p>
              <Button data-testid="inst-cta-open" variant="outline" className="mt-3" onClick={openModal}>
                {t("inst.cta")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="inst-lead-dialog" className="max-w-md">
          <DialogHeader>
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
              <Building2 size={18} />
            </span>
            <DialogTitle>{t("inst.form.title")}</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-slate-600">
              {t("inst.text")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="mt-2 space-y-3">
            <div>
              <Label htmlFor="inst-name">{t("inst.form.name")}</Label>
              <Input id="inst-name" data-testid="inst-field-name" value={form.name} onChange={set("name")} required minLength={2} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="inst-organization">{t("inst.form.organization")}</Label>
              <Input id="inst-organization" data-testid="inst-field-organization" value={form.organization} onChange={set("organization")} required minLength={2} className="mt-1" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="inst-email">{t("inst.form.email")}</Label>
                <Input id="inst-email" data-testid="inst-field-email" type="email" value={form.email} onChange={set("email")} required className="mt-1" />
              </div>
              <div>
                <Label htmlFor="inst-role">{t("inst.form.role")}</Label>
                <Input id="inst-role" data-testid="inst-field-role" value={form.role} onChange={set("role")} required minLength={2} className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="inst-org-type">{t("inst.form.orgtype")}</Label>
              <select
                id="inst-org-type"
                data-testid="inst-field-org-type"
                value={form.org_type}
                onChange={set("org_type")}
                required
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="" disabled>—</option>
                {ORG_TYPES.map((o) => (
                  <option key={o} value={o}>{t(`inst.org.${o}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="inst-message">{t("inst.form.message")}</Label>
              <textarea
                id="inst-message"
                data-testid="inst-field-message"
                value={form.message}
                onChange={set("message")}
                maxLength={2000}
                rows={3}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <label className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
              <input
                type="checkbox"
                data-testid="inst-field-consent"
                checked={form.consent}
                onChange={set("consent")}
                required
                className="mt-0.5"
              />
              {t("inst.form.consent")}
            </label>
            <Button
              data-testid="inst-submit-button"
              type="submit"
              disabled={sending}
              className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800"
            >
              <Send size={15} className="mr-2" />
              {sending ? t("inst.form.sending") : t("inst.form.submit")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
